import { useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBreadcrumbContext } from "./BreadcrumbsProvider";
import { BreadcrumbStackEntry } from "./types";

interface UseBreadcrumbOptions {
  /** Seeded into stack when empty (deep link / reload fallback) */
  defaultParents?: BreadcrumbStackEntry[];
  /** If set, truncate the stack to this pathname before pushing.
   *  Use for tab pages so switching tabs replaces the current tab entry
   *  instead of pushing deeper. */
  truncateTo?: string;
}

/**
 * Stack-based breadcrumb hook. Each page pushes only its OWN breadcrumb.
 * The provider maintains a stack and uses URL matching to push/pop automatically.
 *
 * defaultParents are only seeded when the stack is empty (deep link / page reload).
 * During normal navigation, the existing stack provides the trail context.
 *
 * @example
 * // Root page
 * useBreadcrumb("Warehouses");
 *
 * // Detail page with fallback parents for deep links
 * useBreadcrumb(warehouseName ?? "Loading...", {
 *   defaultParents: [{ label: "Warehouses", pathname: "/warehouse" }],
 * });
 *
 * // Tab page — truncates to parent so tab switches replace the current tab
 * useBreadcrumb("All Queries", {
 *   defaultParents: [{ label: "Code", pathname: "/query" }],
 *   truncateTo: "/query",
 * });
 */
export const useBreadcrumb = (label: string, options?: UseBreadcrumbOptions) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pushBreadcrumb, resetStack } = useBreadcrumbContext();
  const { pathname } = location;

  useLayoutEffect(() => {
    // On mount: check for breadcrumbReset flag (sidebar navigation)
    const state = location.state as { breadcrumbReset?: boolean } | null;
    if (state?.breadcrumbReset) {
      resetStack();
      // Clean up the flag so browser-back doesn't re-trigger
      navigate(pathname, { replace: true, state: { ...state, breadcrumbReset: undefined } });
    }

    // Push this page's breadcrumb, with defaultParents for empty-stack seeding
    pushBreadcrumb({ pathname, label }, options?.defaultParents, options?.truncateTo);

    // NO cleanup — intentional to prevent flash on navigation
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally omit: pushBreadcrumb/resetStack/navigate (stable refs),
    // options.defaultParents/truncateTo (static per page, only used on mount)
  }, [pathname, label]);
};
